import React, { useEffect, useRef } from 'react';
import {
  CANVAS_W, CANVAS_H, PLAYER_X, PLAYER, PLAYER_SPRITE, BIRD_SPRITE,
  freshState, startJump, endJump, step, hits, drawScene,
} from './PokeDash';

// Cheeky Dash playing itself, for the Arcade card. It runs the real game engine with a simple
// auto-player, starts over every so often, and only animates while it's on screen.
const RESTART_AFTER_MS = 40000; // start a fresh run now and then, so it shows the early, easier part too

// The auto-player: jump balls and low Pidgeys, duck middle Pidgeys, run under high ones
function autoPlay(s) {
  const next = s.obstacles.find(o => o.x + o.w > PLAYER_X);
  if (!next) return;
  const dist = next.x - (PLAYER_X + PLAYER.w);
  const lead = s.speed * 5 + 4; // react a few frames before it arrives
  const bird = next.t.type === 'bird';
  if (bird && next.rise === 25) {
    if (dist < lead + 10 && !s.jumping) { s.duckHeld = true; s.ducking = true; }
  } else if (!(bird && next.rise === 50) && dist < lead && dist > -5 && !s.jumping) {
    s.ducking = false; s.duckHeld = false;
    startJump(s);
    s.released = Math.random() < 0.35; // sometimes a short hop, like a real player
    if (s.released) endJump(s);
  }
  if (next.x + next.w < PLAYER_X && s.ducking) { s.ducking = false; s.duckHeld = false; }
}

export default function DashPreview() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const sprites = {};
    const load = (key, src) => { const img = new Image(); img.onload = () => { sprites[key] = img; }; img.src = src; };
    load('player', PLAYER_SPRITE);
    load('bird', BIRD_SPRITE);

    let s = freshState();
    s.runTime = 2400; // skip most of the empty start so obstacles appear quickly
    let started = performance.now();
    let last = null;
    let raf = null;
    let visible = false;
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const frame = (now) => {
      raf = null;
      if (last != null) {
        const dt = Math.min(now - last, 50);
        autoPlay(s);
        step(s, dt);
        if (hits(s) || now - started > RESTART_AFTER_MS) {
          s = freshState(); s.runTime = 2400; started = now;
        }
      }
      last = now;
      drawScene(ctx, s, sprites, now, true);
      if (visible && !still && !document.hidden) raf = requestAnimationFrame(frame);
    };
    const resume = () => {
      if (raf == null && visible && !document.hidden) { last = null; raf = requestAnimationFrame(frame); }
    };

    // Only animate while the card is on screen and the tab is open
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) resume();
    });
    observer.observe(canvas);
    const onVisibility = () => resume();
    document.addEventListener('visibilitychange', onVisibility);
    drawScene(ctx, s, sprites, performance.now(), true); // a first frame straight away
    // Redraw once the sprites have loaded, in case the card is showing a still frame
    const spriteTimer = setTimeout(() => drawScene(ctx, s, sprites, performance.now(), true), 800);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(spriteTimer);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      aria-label="Cheeky Dash playing itself"
      role="img"
      className="block w-full h-full"
      style={{ objectFit: 'cover', imageRendering: 'pixelated' }}
    />
  );
}
